import { useEffect, useMemo, useState } from "react";
import { Camera, Images, ScanLine, Settings as SettingsIcon, SlidersHorizontal } from "lucide-react";
import CameraView from "./components/CameraView";
import PhotoEditor from "./components/PhotoEditor";
import ScannerView from "./components/ScannerView";
import Gallery from "./components/Gallery";
import ThemeCenter from "./components/ThemeCenter";
import Settings from "./components/Settings";
import { getAll, getSetting, saveSetting } from "./lib/indexedDb";
import { applyTheme } from "./lib/theme";
import { registerServiceWorker } from "./lib/pwa";
import { installButtonSounds } from "./lib/sound";

const DEFAULT_SETTINGS = {
  defaultCamera:   "environment",
  saveOriginal:    true,
  saveEdited:      true,
  exportFormat:    "jpg",
  defaultFilter:   "clean-pro",
  grid:            false,
  mirrorSelfie:    true,
  theme:           "premium-dark",
  accent:          "Teal",
  motion:          "full",
  soundEffects:    true,
  hapticFeedback:  true,
  autoEnhance:     true,
  aiModel:         "Server default",
  saveAiHistory:   true,
  autoScan:        false,
  defaultScanMode: "qr",
};

const NAV = [
  { id: "camera",   label: "Camera",   icon: Camera            },
  { id: "studio",   label: "Studio",   icon: SlidersHorizontal },
  { id: "scanner",  label: "Scanner",  icon: ScanLine          },
  { id: "gallery",  label: "Gallery",  icon: Images            },
  { id: "settings", label: "Settings", icon: SettingsIcon      },
];

export default function App() {
  const initialTab = new URLSearchParams(location.search).get("tab") || "camera";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [latestPhoto, setLatestPhoto] = useState(null);
  const [galleryRefresh, setGalleryRefresh] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    getSetting("appSettings", DEFAULT_SETTINGS).then((stored) => {
      const merged = { ...DEFAULT_SETTINGS, ...stored };
      setSettings(merged);
      applyTheme(merged.theme, merged.accent, merged.motion);
    });
    getAll("photos").then((photos) => setLatestPhoto(photos.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0] || null));
    registerServiceWorker(() => setUpdateReady(true)).catch(() => undefined);
  }, []);

  useEffect(() => installButtonSounds(settings.soundEffects), [settings.soundEffects]);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Portrait lock only — fullscreen is handled by manifest "display": "fullscreen"
    // so we never call requestFullscreen() from JS (that API triggers the
    // "drag from top to exit" browser notification banner).
    const lockPortrait = async () => {
      try {
        if (!cancelled && screen.orientation?.lock) {
          await screen.orientation.lock("portrait-primary");
        }
      } catch {
        // Orientation lock requires an installed / fullscreen PWA context on some browsers.
      }
    };

    lockPortrait();
    window.addEventListener("orientationchange", lockPortrait);
    screen.orientation?.addEventListener?.("change", lockPortrait);

    return () => {
      cancelled = true;
      window.removeEventListener("orientationchange", lockPortrait);
      screen.orientation?.removeEventListener?.("change", lockPortrait);
    };
  }, []);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    applyTheme(next.theme, next.accent, next.motion);
    await saveSetting("appSettings", next);
  };

  const currentScreen = useMemo(() => {
    if (activeTab === "camera") {
      return (
        <CameraView
          settings={settings}
          latestPhoto={latestPhoto}
          online={online}
          onNavigate={setActiveTab}
          onPhotoCaptured={(photo) => {
            setLatestPhoto(photo);
            setGalleryRefresh((value) => value + 1);
          }}
        />
      );
    }
    if (activeTab === "studio") return <PhotoEditor photo={latestPhoto} onSaved={(photo) => { setLatestPhoto(photo); setGalleryRefresh((value) => value + 1); }} />;
    if (activeTab === "scanner") return <ScannerView settings={settings} online={online} />;
    if (activeTab === "gallery") return <Gallery refreshKey={galleryRefresh} onEdit={(photo) => { setLatestPhoto(photo); setActiveTab("studio"); }} />;
    if (activeTab === "theme") return <ThemeCenter settings={settings} onChange={saveSettings} />;
    return <Settings settings={settings} onChange={saveSettings} onThemeCenter={() => setActiveTab("theme")} />;
  }, [activeTab, galleryRefresh, latestPhoto, online, settings]);

  return (
    <main className={`app-shell orientation-locked${activeTab === "camera" ? " camera-route" : ""}`}>
      {currentScreen}
      {activeTab !== "camera" && (
        <nav className="bottom-nav safe-bottom" aria-label="Main navigation">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.id} type="button" className={activeTab === item.id ? "is-active" : ""} onClick={() => setActiveTab(item.id)}>
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
      {updateReady && (
        <button className="update-toast" type="button" onClick={() => location.reload()}>
          New version ready. Tap to update.
        </button>
      )}
    </main>
  );
}
